import {
    moviePromoContentActionsQuery,
    MoviePromoContentActions,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createResourceQuery } from '@funtech-int/rival';
import { useResources, useSignal } from '@funtech-int/rival-react';
import { useAuthManager } from '@funtech-int/yango-shared';

import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { GraphqlClient } from '@/shared/lib/graphqlClient';
import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';
import { useInMemoryCache } from '@/shared/hooks/useInMemoryCache';
import { InMemoryCache } from '@/shared/lib/inMemoryCache';

import { getMovieUGCKey } from '../../../shared/lib/movieUGC';
import { watchingProgressResourceQuery } from '../lib/watchingProgressResource';

interface MoviePromoContentActionsVariables {
    contentId: string;
    isAuth: boolean;
    billingFeaturesCount: number;
}

interface GQLQueryParams {
    client: GraphqlClient;
    inMemoryCache: InMemoryCache;
    variables: MoviePromoContentActionsVariables;
}

const moviePromoContentActionsResourceQuery = createResourceQuery<
    GQLQueryParams,
    MoviePromoContentActions | undefined | null
>({
    key(ctx) {
        return [
            moviePromoContentActionsQuery.name,
            ctx.variables.contentId,
            ctx.variables.billingFeaturesCount,
        ].join(':');
    },
    fetch(ctx) {
        return ctx.client
            .fetch({
                operation: moviePromoContentActionsQuery,
                variables: {
                    contentId: ctx.variables.contentId,
                    isAuth: ctx.variables.isAuth,
                },
            })
            .then(({ data }) => {
                if (data) {
                    ctx.inMemoryCache.set(
                        getMovieUGCKey(data.id, 'plannedToWatch'),
                        Boolean(data.userData?.isPlannedToWatch)
                    );
                    ctx.inMemoryCache.set(
                        getMovieUGCKey(data.id, 'notInterested'),
                        Boolean(data.userData?.isNotInterested)
                    );
                }

                return data;
            });
    },
    toJSON(data) {
        return data as any;
    },
    hydrate(data: any) {
        return moviePromoContentActionsQuery.hydrate(data) as any;
    },
});

interface UseMoviePromoContentActionsQueryParams {
    contentId: string;
}

export function useMoviePromoContentActions(params: UseMoviePromoContentActionsQueryParams) {
    const client = useGraphqlClient();
    const inMemoryCache = useInMemoryCache();
    const billingFeaturesCount = useSignal(useProfileManager().billingFeatureNames).length;
    const { isAuthorized } = useAuthManager();

    const [contentActions, watchingProgress] = useResources(
        moviePromoContentActionsResourceQuery({
            client,
            inMemoryCache,
            variables: {
                contentId: params.contentId,
                billingFeaturesCount,
                isAuth: isAuthorized,
            },
        }),
        ...(isAuthorized
            ? [
                  watchingProgressResourceQuery({
                      client,
                      variables: {
                          contentId: params.contentId,
                          billingFeaturesCount,
                      },
                  }),
              ]
            : [])
    );

    return { contentActions, watchingProgress };
}
